import {
  BlueprintQuoteResponse,
  BlueprintQuoteScope,
  MCustomSelectProps,
} from '@monetize/types/app';
import React, { useMemo } from 'react';
import {
  CUSTOM_SELECT_SUBTITLE_TYPE,
  OPTION_TYPE_FIELD,
} from '../../constants/customSelect';
import { MBox, MText } from '../Monetize';
import { MainSearchInput } from '../Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

interface BlueprintSelectProps extends MCustomSelectProps {
  value: string;
  onChange: (blueprint: BlueprintQuoteResponse | null) => void;
  isDisabled?: boolean;
  currency: string;
}

export const BlueprintSelect = React.forwardRef<
  HTMLInputElement,
  BlueprintSelectProps
>(
  (
    {
      value,
      onChange,
      isDisabled = false,
      currency,
      ...rest
    }: BlueprintSelectProps,
    ref,
  ) => {
    const transformDataFromApi = (blueprints: BlueprintQuoteResponse[]) => {
      if (!blueprints || !Array.isArray(blueprints)) {
        return [];
      }

      const userBlueprints = blueprints
        .filter(({ scope }) => {
          return scope === BlueprintQuoteScope.USER;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      const tenantBlueprints = blueprints
        .filter(({ scope }) => {
          return scope === BlueprintQuoteScope.TENANT;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      return [
        ...(userBlueprints.length > 0
          ? [
              {
                [OPTION_TYPE_FIELD]: CUSTOM_SELECT_SUBTITLE_TYPE,
                name: 'Your Blueprints',
              },
            ]
          : []),
        ...userBlueprints,
        ...(tenantBlueprints.length > 0
          ? [
              {
                [OPTION_TYPE_FIELD]: CUSTOM_SELECT_SUBTITLE_TYPE,
                name: 'Tenant Blueprints',
              },
            ]
          : []),
        ...tenantBlueprints,
      ];
    };

    const renderItemContent = ({
      title,
      item,
      isSubtitle,
    }: {
      title: string;
      item:
        | BlueprintQuoteResponse
        | {
            [OPTION_TYPE_FIELD]: string;
            name: string;
          };
      isSubtitle: boolean;
      isSelected: boolean;
      isHighlight: boolean;
      isDisabled: boolean;
      query: string;
    }) => {
      if (isSubtitle) {
        return (
          <MText
            color="tGray.darkPurple"
            fontWeight="400"
            fontSize="xs"
            display="block"
            mt="0"
            lineClamp={1}
          >
            {title}
          </MText>
        );
      }
      const blueprint = item as BlueprintQuoteResponse;
      return (
        <MBox>
          <MText fontWeight="medium">{blueprint?.name}</MText>
          {!!blueprint?.description && (
            <MText fontSize="sm" color="gray.600" mt={0.5}>
              {blueprint?.description}
            </MText>
          )}
        </MBox>
      );
    };

    const additionalSearchParams = useMemo(
      () => ({
        isActive: 'true',
        ...(currency ? { quoteCurrency: currency } : {}),
      }),
      [currency],
    );

    return (
      <MCustomSelect
        ref={ref}
        value={value}
        onChange={onChange}
        endpoint="/api/blueprintQuotes"
        itemTitle="name"
        itemValue="id"
        getByIdEndpointFn={(id) => `/api/blueprintQuotes/${id}`}
        returnItem
        clearable
        useMainInputAsSearchInput
        MainInputComponent={MainSearchInput}
        isDisabled={isDisabled}
        transformDataFromApi={transformDataFromApi}
        renderItemContent={renderItemContent}
        additionalSearchParams={additionalSearchParams}
        {...rest}
      />
    );
  },
);
